import { useRef, useState } from "react";
import dayjs from "dayjs";
import { alpha } from "@mui/material/styles";
import { Box, CircularProgress, Grid, Paper } from "@mui/material";
import { CalendarNext, CalendarPrev, CalendarToday, Eventcalendar } from "@mobiscroll/react";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";
import { useIsFetching, useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";

import { zohoApi } from "zohoApi";
import { useGetEvents } from "hook/use-get-events";
import { CreateDialog } from "./create-dialog";
import { DeleteDialog } from "./delete-dialog";
import { EditDialog } from "./edit-dialog";
import { CalenderLeft } from "./calender-left";
import { useEventConfigArray } from "hook/use-event-config-array";
import { enqueueSnackbar } from "notistack";
import { useUpdateEventTime } from "hook/use-update-event-time";

const DAY = "day";
const WEEK = "week";
const MONTH = "month";

const getFilteredEvents = ({ myEvents, resources, selectedSettings }) => {
  const settingIds = selectedSettings?.map((el) => el?.setting_id);
  const resourceIds = resources?.map((el) => el.id);
  const filtered = myEvents?.filter((ev) => {
    return resourceIds.includes(ev.resource) && settingIds.includes(ev.setting_id);
  });
  return filtered;
};

const mainCalenderView = ({ singleUser, dayView, weekView, monthView }) => {
  if (dayView && singleUser) {
    return { schedule: { type: "day" } };
  }

  if (dayView && !singleUser) {
    return { timeline: { type: "day" } };
  }

  if (weekView && singleUser) {
    return { schedule: { type: "week" } };
  }

  if (weekView && !singleUser) {
    return {
      timeline: {
        type: "week",
        startDay: 1,
        endDay: 0,
        eventList: true,
      },
    };
  }

  if (monthView) {
    return { calendar: { labels: true } };
  }
  //if there is no selected view in setting
  return { calendar: { labels: true } };
};

export const Calender = ({ resources, selectedSettings, selectedView }) => {
  const queryClient = useQueryClient();
  const isFetching = useIsFetching();
  const mutate = useUpdateEventTime();
  const isMutating = useIsMutating({ mutationKey: ["update-item"] });
  const settingArray = useEventConfigArray();
  const [newEvent, setNewEvent] = useState(null); // holds the clicked time
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [firstDay, setFirstDay] = useState();
  const [lastDay, setLastDay] = useState();
  const [hideTodayButton, setHideTodayButton] = useState(false);

  const refetchData = (key) => {
    if (key) {
      queryClient.invalidateQueries({
        queryKey: ["eventArray", key],
        exact: true,
        refetchType: "all",
      });
    }
  };

  const add12days = (date) => {
    // 12 days added to fill rest of the cell in calender
    return dayjs(date).add(12, "day").format("YYYY-MM-DD");
  };

  const sub12days = (date) => {
    // 12 days subtracted to fill rest of the cell in calender
    return dayjs(date).subtract(12, "day").format("YYYY-MM-DD");
  };

  const dayView = selectedView === DAY;
  const weekView = selectedView === WEEK;
  const monthView = selectedView === MONTH;

  const singleUser = resources?.length === 1;
  const singleUserDayView = singleUser && dayView;

  const { data: myEvents } = useGetEvents({
    startTimeValue: sub12days(firstDay),
    endTimeValue: add12days(lastDay),
    selectedSettings,
  });

  console.log(firstDay, { myEvents }, lastDay);

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleOpenDeleteDialog = (event) => {
    setSelectedEvent(event);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteEvent = async () => {
    const recordId = selectedEvent?.id;
    const moduleApiName = selectedEvent?.moduleApiName;
    await zohoApi.record.deleteRecord({ moduleApiName, recordId });
    refetchData(selectedEvent?.configKey);
    setDeleteDialogOpen(false);
    // if (title.trim() && newEvent) {
    //   setMyEvents((prev) => [...prev, { ...newEvent, title: title.trim() }]);
    // }
    // setTitle("");
    // setNewEvent(null);
    // setCreateDialogOpen(false);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  return (
    <div style={{ height: "100%", overflow: "auto" }}>
      <Grid container spacing={1.5} sx={{ height: "100%" }}>
        {singleUserDayView ? (
          <CalenderLeft
            firstDay={firstDay}
            setFirstDay={setFirstDay}
            lastDay={lastDay}
            setLastDay={setLastDay}
            setHideTodayButton={setHideTodayButton}
          />
        ) : null}
        <Grid
          size={singleUserDayView ? 9.5 : 12}
          sx={{
            borderRadius: "10px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Paper
              elevation={1}
              sx={{
                flexGrow: 1,
                "& .mbsc-schedule-date-header-text": {
                  display: "none !important",
                },
              }}
            >
              <Box className="mbsc-grid mbsc-no-padding" sx={dayView || weekView ? {} : { height: "100%" }}>
                <Box className="mbsc-row" sx={dayView || weekView ? {} : { height: "100%" }}>
                  <Box className="mbsc-col-sm-12 docs-appointment-calendar">
                    <Eventcalendar
                      data={getFilteredEvents({
                        myEvents,
                        resources,
                        selectedSettings,
                      })}
                      resources={resources}
                      view={mainCalenderView({
                        singleUser,
                        dayView,
                        weekView,
                        monthView,
                      })}
                      selectedDate={firstDay}
                      onPageLoaded={(e, inst) => {
                        let firstDay;
                        let lastDay;
                        let hideButton;

                        if (dayView) {
                          firstDay = dayjs(e.firstDay).format("YYYY-MM-DD");
                          lastDay = dayjs(e.firstDay).format("YYYY-MM-DD");
                          hideButton = dayjs(firstDay).format("D-MMM-YYYY") === dayjs().format("D-MMM-YYYY");
                        }

                        if (weekView) {
                          firstDay = dayjs(e.firstDay).format("YYYY-MM-DD");
                          lastDay = dayjs(e.lastDay).subtract(1, "day").format("YYYY-MM-DD");
                          hideButton = dayjs().isBetween(dayjs(firstDay), dayjs(firstDay).add(6, "day"), null, "[]");
                        }

                        if (monthView) {
                          const viewDate = new Date(inst._viewDate || inst._active);
                          firstDay = dayjs(new Date(viewDate.getFullYear(), viewDate.getMonth(), 1)).format("YYYY-MM-DD");
                          lastDay = dayjs(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0)).format("YYYY-MM-DD");
                          hideButton = dayjs().isBetween(dayjs(firstDay), dayjs(firstDay).endOf("month"), null, "[]");
                        }

                        if (firstDay && lastDay) {
                          setFirstDay(firstDay);
                          setLastDay(lastDay);
                          setHideTodayButton(hideButton);
                        }
                      }}
                      renderHeader={() => {
                        return (
                          <Box
                            component="span"
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              p: "3px 13px",
                              width: "100%",
                              bgcolor: "white",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                fontWeight: 600,
                                fontSize: "18px",
                                color: "rgba(0,0,0,.77)",
                                display: "block",
                              }}
                            >
                              {dayjs(firstDay).format("dddd, MMM D, YYYY")}
                            </Box>
                            <Box
                              component="span"
                              sx={(theme) => ({
                                display: "flex",
                                gap: "5px",
                                "& .cal-header-prev, .cal-header-next, .cal-header-today": {
                                  bgcolor: alpha(theme.palette.primary.light, 0.3),
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  borderRadius: "4px",
                                  height: "26px !important",
                                },
                                "& .cal-header-prev, .cal-header-next": {
                                  width: "26px",
                                },
                                "& .cal-header-today": { px: "8px" },
                                "& svg": { fill: "primary.main" },
                                "& button": {
                                  color: `${theme.palette.primary.main} !important`,
                                },
                              })}
                            >
                              <CalendarPrev className="cal-header-prev" />
                              {hideTodayButton ? null : <CalendarToday className="cal-header-today" />}
                              <CalendarNext className="cal-header-next" />
                            </Box>
                          </Box>
                        );
                      }}
                      themeVariant="light"
                      //--------
                      dragToMove={true}
                      dragToResize={true}
                      dragToCreate={true}
                      editable={true}
                      onEventUpdate={(args) => {
                        const { start, end, ...rest } = args.event;
                        const data = { ...rest };
                        data.startTime = dayjs(start);
                        data.endTime = dayjs(end);
                        data.selectedActivity = settingArray?.find((el) => el?.setting_id === data.setting_id);
                        //---------
                        mutate(data);
                      }}
                      //--------
                      clickToCreate={true}
                      onEventCreate={(args) => {
                        setNewEvent(args.event); // contains start, end
                        setCreateDialogOpen(true);
                        return false; // prevent default auto-create
                      }}
                      onEventClick={(args) => {
                        // console.log(args.event);
                        setSelectedEvent(args.event); // contains start, end
                        setDetailsDialogOpen(true);
                        return false; // prevent default auto-create
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>
        {isFetching || isMutating ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(240, 240, 240, 0.5)",
              zIndex: 2,
              display: "grid",
              placeItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : null}
      </Grid>

      {createDialogOpen && false ? (
        <CreateDialog
          dialogOpen={createDialogOpen}
          handleCloseDialog={handleCloseCreateDialog}
          selectedEvent={selectedEvent}
          revalidate={() => {
            queryClient.invalidateQueries({
              queryKey: ["eventArray"],
            });
          }}
        />
      ) : null}

      {detailsDialogOpen ? (
        <EditDialog
          dialogOpen={detailsDialogOpen}
          handleCloseDialog={handleCloseDetailsDialog}
          selectedEvent={selectedEvent}
          handleOpenDeleteDialog={handleOpenDeleteDialog}
          refetchData={refetchData}
          //
          // handleConfirmEvent={handleDetailsEvent}
        />
      ) : null}

      {deleteDialogOpen ? (
        <DeleteDialog dialogOpen={deleteDialogOpen} handleCloseDialog={handleCloseDeleteDialog} handleConfirmEvent={handleDeleteEvent} />
      ) : null}
    </div>
  );
};
